import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { useChartAnalysis } from '@/hooks/useChartAnalysis';
import { AIInsights } from './AIInsights';

interface CustomChartWidgetProps {
  config: {
    chartType?: string;
    data?: any[];
    title?: string;
  };
  onInsightsToggle?: (isOpen: boolean) => void;
}

const COLORS = [
  'hsl(220 91% 42%)',  // Bleu professionnel
  'hsl(142 76% 36%)',  // Vert
  'hsl(35 90% 55%)',   // Orange
  'hsl(0 75% 55%)',    // Rouge
  'hsl(271 91% 65%)',  // Violet
  'hsl(45 93% 47%)',   // Jaune
  'hsl(340 82% 52%)',  // Rose
  'hsl(200 98% 39%)',  // Cyan
  'hsl(20 90% 48%)',   // Orange brûlé
  'hsl(280 87% 47%)',  // Magenta
];

export const CustomChartWidget: React.FC<CustomChartWidgetProps> = ({ config, onInsightsToggle }) => {
  const { chartType = 'bar', data = [], title = 'Graphique personnalisé' } = config;
  const [isAIPanelOpen, setIsAIPanelOpen] = React.useState<boolean>(false);
  const { 
    isAnalyzing, 
    insights, 
    analyzeChart, 
    clearInsights,
    quota,
    analysisAge,
    isCached,
  } = useChartAnalysis(chartType, title);

  // Charger l'état du panneau depuis localStorage
  React.useEffect(() => {
    const savedState = localStorage.getItem(`ai-panel-${chartType}-${title}`);
    if (savedState !== null) {
      setIsAIPanelOpen(savedState === 'true');
    }
  }, [chartType, title]);

  // Sauvegarder l'état du panneau
  const toggleAIPanel = () => {
    const newState = !isAIPanelOpen;
    setIsAIPanelOpen(newState);
    localStorage.setItem(`ai-panel-${chartType}-${title}`, String(newState));
    onInsightsToggle?.(newState);
  };

  // Analyser le graphique
  const handleAnalyzeChart = () => {
    if (!data || data.length === 0) {
      return;
    }
    analyzeChart(data, chartType, title);
    setIsAIPanelOpen(true);
    localStorage.setItem(`ai-panel-${chartType}-${title}`, 'true');
  };

  // Régénérer l'analyse (forcer une nouvelle analyse)
  const handleRegenerateAnalysis = () => {
    if (!data || data.length === 0) {
      return;
    }
    analyzeChart(data, chartType, title, true);
  };

  if (!data || data.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <h3 className="font-medium mb-2">{title}</h3>
        <p>Aucune donnée disponible</p>
      </div>
    );
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        const dataWithColors = data.map((item, index) => ({
          ...item,
          color: COLORS[index % COLORS.length]
        }));
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataWithColors}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value">
                {dataWithColors.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
           <ResponsiveContainer width="100%" height={500}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={140}
                fill="hsl(var(--primary))"
                label
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div className="p-4 text-center text-muted-foreground">
            Type de graphique non supporté: {chartType}
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{title}</h3>
        {insights ? (
          <Button
            variant={isAIPanelOpen ? "default" : "outline"}
            size="sm"
            onClick={toggleAIPanel}
            className="group relative overflow-hidden bg-gradient-to-r from-[hsl(var(--ai-primary)/0.1)] to-[hsl(var(--ai-accent)/0.1)] border-[hsl(var(--ai-primary)/0.3)] hover:border-[hsl(var(--ai-primary)/0.5)] transition-all duration-300 hover:shadow-[0_0_20px_hsl(var(--ai-glow)/0.3)]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary)/0.2)] to-[hsl(var(--ai-accent)/0.2)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-center gap-2">
              <Sparkles className="h-4 w-4 group-hover:scale-110 transition-transform duration-300 text-[hsl(var(--ai-primary))]" />
              <span className="font-medium">{isAIPanelOpen ? 'Masquer insights' : 'Afficher insights'}</span>
              {isCached && analysisAge !== null && analysisAge > 7 && (
                <span className="ml-2 text-xs text-muted-foreground">({analysisAge}j)</span>
              )}
            </div>
          </Button>
        ) : quota.remaining > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAnalyzeChart}
            disabled={isAnalyzing}
            className="group relative overflow-hidden bg-gradient-to-r from-[hsl(var(--ai-primary)/0.1)] to-[hsl(var(--ai-accent)/0.1)] border-[hsl(var(--ai-primary)/0.3)] hover:border-[hsl(var(--ai-primary)/0.5)] transition-all duration-300 hover:shadow-[0_0_20px_hsl(var(--ai-glow)/0.3)] disabled:opacity-50"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--ai-primary)/0.2)] to-[hsl(var(--ai-accent)/0.2)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative flex items-center gap-2">
              <div className="relative">
                <Sparkles className={`h-4 w-4 ${isAnalyzing ? 'animate-spin' : 'group-hover:scale-110 transition-transform duration-300'} text-[hsl(var(--ai-primary))]`} />
                {isAnalyzing && <div className="absolute inset-0 bg-[hsl(var(--ai-glow))] blur-md opacity-50" />}
              </div>
              <span className={`font-medium ${isAnalyzing ? 'text-[hsl(var(--ai-primary))]' : 'bg-gradient-to-r from-[hsl(var(--ai-primary))] to-[hsl(var(--ai-accent))] bg-clip-text text-transparent'}`}>
                {isAnalyzing ? 'Analyse...' : `Analyser (${quota.remaining}/${quota.limit})`}
              </span>
            </div>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled
            className="opacity-50 cursor-not-allowed"
            title={`Quota quotidien atteint (${quota.used}/${quota.limit})`}
          >
            <Sparkles className="h-4 w-4 mr-2 text-muted-foreground" />
            <span className="font-medium">Quota atteint</span>
          </Button>
        )}
      </div>

      <div className={`grid gap-4 ${insights && isAIPanelOpen ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        <div className={insights && isAIPanelOpen ? 'lg:col-span-1' : 'col-span-1'}>
          {renderChart()}
        </div>

        {insights && isAIPanelOpen && (
          <div className="lg:col-span-1 h-[400px]">
            <AIInsights 
              insights={insights} 
              onClose={clearInsights}
              onRegenerate={handleRegenerateAnalysis}
            />
          </div>
        )}
      </div>
    </div>
  );
};